import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { plural } from '@/components/admin/sessions/walk/walk-labels';
import { cn } from '@/lib/utils';
import type { StepRun } from '@matter_extractor/shared';

/**
 * The chain of steps under the protocol block (§12.2) - the mini-chaîne of a
 * protocol card (§7.1), annotated with what this session did to it.
 *
 * ## It is the PROTOCOL's order, never the session's
 *
 * `stepRuns` draws every step of the frozen protocol in the order it carries
 * them, and says so: a session that jumped back to step 2 walked `1, 2, 3, 2`,
 * and drawing that would make one protocol look like four steps. What the
 * session did shows in the annotations instead, where a replay reads « 2 essais ».
 *
 * ## The three annotations, and the one that only appears sometimes
 *
 * Turns played, objectives met over objectives carried, and the number of
 * attempts WHEN IT IS ABOVE ONE (§12.2). One attempt is what every step gets, so
 * printing it would be printing the default on every step of every session, and
 * the figure that matters - a step that had to be replayed - would be the one
 * nobody notices.
 *
 * A step the session never entered is drawn faint and says so: it is a step of
 * the protocol that this conversation did not reach, which is a reading, not a
 * hole. Colour carries the verdict (`vert` complete, `rouge` entered and short
 * of its objectives) and it is never the only carrier - the annotation under the
 * name says the same thing in words.
 */

export interface StepChainProps {
  readonly runs: readonly StepRun[];
}

/** What a step of the chain says under its name. */
function annotation(run: StepRun): string {
  if (!run.entered) return 'jamais atteinte';
  const objectives = `${plural(run.objectivesSucceeded, 'objectif')} sur ${run.objectivesTotal}`;
  const attempts = run.attempts > 1 ? ` · ${plural(run.attempts, 'essai')}` : '';
  return `${plural(run.turnCount, 'tour')} · ${objectives}${attempts}`;
}

export function StepChain({ runs }: StepChainProps) {
  return (
    <ol className="flex list-none flex-wrap items-stretch gap-1 p-0">
      {runs.map((run, position) => (
        <li key={run.stepId} className="flex items-stretch">
          {position === 0 ? null : (
            <span aria-hidden="true" className="self-center px-2 text-gray-400">
              →
            </span>
          )}
          <div
            data-testid={SESSIONS_TEST_IDS.chainStep}
            data-step-id={run.stepId}
            className={cn(
              'flex min-w-0 flex-col gap-0.5 rounded-md border bg-white px-3 py-2',
              !run.entered
                ? 'border-gray-200 opacity-60'
                : run.complete
                  ? 'border-vert'
                  : 'border-rouge-deep',
            )}
          >
            <span className="font-semibold text-caption text-gray-900">
              {run.entered ? (
                <span
                  aria-hidden="true"
                  className={cn(
                    'mr-1.5 inline-block size-2 rounded-full',
                    run.complete ? 'bg-vert' : 'bg-rouge-deep',
                  )}
                />
              ) : null}
              {run.index} · {run.name}
            </span>
            <span className="text-gray-600 text-micro tabular-nums">{annotation(run)}</span>
          </div>
        </li>
      ))}
    </ol>
  );
}
